import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/Badges";
import { euro, cashLine } from "@/components/CashFields";
import { VerifyBanner } from "@/components/VerifyBanner";
import { Loading, EmptyState } from "@/components/States";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Repeat, ArrowRight } from "lucide-react";

function Row({ p, meId }) {
  const isReceiver = meId === p.to_user_id;
  const other = isReceiver ? p.from_user : p.to_user;
  return (
    <Link to={`/proposte/${p.id}`} data-testid="proposal-row" className="card-lift flex items-center justify-between rounded-xl border border-[#2A3655] bg-[#141B2D] p-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2"><p className="font-display font-semibold text-[#F8FAFC]">@{other?.username || "utente"}</p><StatusBadge status={p.status} />{p.cash_cents > 0 && <span className="rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-1.5 py-0.5 text-[11px] text-[#F59E0B]">{euro(p.cash_cents)}</span>}</div>
        <p className="mt-1 text-xs text-[#94A3B8]">Offerte {p.offered.length} · richieste {p.requested.length} · v{p.version}</p>
      </div>
      <ArrowRight className="h-5 w-5 shrink-0 text-[#94A3B8]" />
    </Link>
  );
}

function List({ items, meId, empty }) {
  if (items === null) return <Loading />;
  if (items.length === 0) return <EmptyState icon={Repeat} title="Niente qui" description={empty} />;
  return <div className="space-y-2">{items.map((p) => <Row key={p.id} p={p} meId={meId} />)}</div>;
}

export default function Proposals() {
  const { user } = useAuth();
  const [received, setReceived] = useState(null);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (!user?.email_verified) { setReceived([]); setSent([]); return; }
    api.get("/proposals/received").then((r) => setReceived(r.data)).catch(() => setReceived([]));
    api.get("/proposals/sent").then((r) => setSent(r.data)).catch(() => setSent([]));
  }, [user]);

  const meId = user?.id;
  const all = useMemo(() => {
    const map = {};
    [...(received || []), ...(sent || [])].forEach((p) => { map[p.id] = p; });
    return Object.values(map);
  }, [received, sent]);
  const toRespond = useMemo(() => (received || []).filter((p) => p.status === "inviata"), [received]);
  const inProgress = useMemo(() => all.filter((p) => p.status === "accettata"), [all]);
  const done = useMemo(() => all.filter((p) => p.status === "completata"), [all]);
  const problems = useMemo(() => all.filter((p) => p.status === "contestata"), [all]);

  const TABS = [
    ["received", "Ricevute", received, "Quando qualcuno ti proporrà uno scambio, lo vedrai qui."],
    ["sent", "Inviate", sent, "Trova un match o un collezionista e invia la tua prima proposta."],
    ["respond", "Da rispondere", toRespond, "Nessuna proposta in attesa di una tua risposta."],
    ["progress", "In corso", inProgress, "Nessuna trattativa accettata in corso."],
    ["done", "Concluse", done, "Nessuno scambio concluso, per ora."],
    ["problems", "Con problemi", problems, "Nessuna trattativa con problemi aperti."],
  ];

  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-[#F8FAFC]">Le mie trattative</h1>
      <p className="mt-1 text-[#CBD5E1]">Gestisci proposte, controproposte e scambi in corso: tutta la trattativa avviene nel sito.</p>
      <div className="mt-6"><VerifyBanner /></div>
      <Tabs defaultValue="received">
        <TabsList className="flex flex-wrap gap-1 bg-[#141B2D] border border-[#2A3655]">
          {TABS.map(([v, label, list]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white">
              {label}{v === "respond" && toRespond.length > 0 && <span className="ml-1 rounded-full bg-[#EF4444] px-1.5 text-[10px] text-white" data-testid="respond-count">{toRespond.length}</span>}
            </TabsTrigger>
          ))}
        </TabsList>
        {TABS.map(([v, label, list, empty]) => (
          <TabsContent key={v} value={v} className="mt-4"><List items={list} meId={meId} empty={empty} /></TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
